import React, { FC, Fragment, useEffect, useState } from 'react';
import { Organization } from 'models/organization';
import { LinkedOrganization, User } from 'models/user';
import { TableCell, TableRow, Button, Typography, Chip, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

type UserItemProps = {
    user: User;
    onUpdateUser: (user: User) => void;
    onLoginClick?: (user: User) => void;
    onGenerateOtpClick?: (user: User) => void;
};

const UserItem: FC<UserItemProps> = ({ user, onUpdateUser, onLoginClick, onGenerateOtpClick }: UserItemProps) => {
    const { t: translate } = useTranslation('common');
    const [userOrganization, setUserOrganization] = useState({} as LinkedOrganization);

    useEffect(() => {
        if (user) {
            setUserOrganization({
                organization: user.organization as Organization,
                role: user.role,
                active: user.active || false,
                type: user.type,
            });
        }
    }, [user]);

    const handleLoginClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();
        if (onLoginClick) {
            onLoginClick(user);
        }
    };

    const handleGenerateOtpClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();
        if (onGenerateOtpClick) {
            onGenerateOtpClick(user);
        }
    };

    const getUserType = (user: User) => {
        if (user.local) {
            return 'MY_CLIENT';
        }

        return 'DENTHUB_CONNECTED';
    };

    return (
        <Fragment>
            <TableRow
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => onUpdateUser(Object.assign({}, user, userOrganization))}
            >
                <Fragment>
                    <TableCell sx={{ width: 300 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                            {/* Name */}
                            <Typography variant="body2" noWrap>
                                {user.fullName}
                            </Typography>
                            {user.type === 'dentist' && (
                                <Typography variant="caption" color="grey.500" noWrap>
                                    {translate(`client-directory:${getUserType(user)}`)}
                                </Typography>
                            )}

                            {/* Shared chip */}
                            {user.isShared && (
                                <Chip
                                    label={translate('common:SHARED_USER')}
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                />
                            )}
                        </Box>
                    </TableCell>

                    <TableCell>{user.email}</TableCell>
                    <TableCell>{translate(userOrganization.role)}</TableCell>
                    <TableCell>
                        {user.linkedOrganizations
                            .map((linkedOrganization: LinkedOrganization) => {
                                const org = linkedOrganization.organization as Organization | null;
                                if (!org) return 'NA';
                                if (org.type === 'lab') {
                                    return `${org.name}`;
                                }
                                return `${org.name} (${org.local ? translate(`client-directory:MY_CLIENT`) : translate(`client-directory:DENTHUB_CONNECTED`)})`;
                            })
                            .join(', ')}
                    </TableCell>

                    <TableCell>
                        <Box display="flex" gap={1}>
                            <Button onClick={handleLoginClick}>{translate('users:LOGIN')}</Button>
                            {onGenerateOtpClick && (
                                <Button onClick={handleGenerateOtpClick} data-test="users.generate-otp">
                                    {translate('users:GENERATE_OTP')}
                                </Button>
                            )}
                        </Box>
                    </TableCell>
                </Fragment>
            </TableRow>
        </Fragment>
    );
};

export default UserItem;
